import { useGetProjectsQuery, useOrganizations } from '@consigli/hooks';
import { Country, CreateProjectRequest, Project, ProjectInvoiceTo } from '@consigli/types';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';

import { SheetForm } from '@/components';
import { validateProjectForm } from '@/pages/projects/projects-form/validator';

import { BasicInformationFields } from './base-fieldset';
import { BillingInformationFields } from './billing-fieldset';
import { ProjectDetailsFields } from './details-fieldset';
import { useFormOptions } from './form-options';
import { OracleFields } from './oracle-fieldset';
import { ServicesFields } from './services-fieldset';
import { CreateProjectFormData } from '../types';

interface ProjectFormProps {
  onSubmit: (data: CreateProjectRequest) => Promise<void>;
  isSubmitting: boolean;
  onCancel: () => void;
  trigger: React.ReactNode;
  onFormChange: (formData: CreateProjectFormData) => void;
  onValidationChange?: (
    isValid: boolean,
    errors: Partial<Record<keyof CreateProjectFormData, string>>,
  ) => void;
  validationErrors?: Partial<Record<keyof CreateProjectFormData, string>>;
}

const defaultValues: CreateProjectFormData = {
  name: '',
  address: '',
  country: Country.NORWAY,
  specifications: [],
  constructionTypes: [],
  organizationId: '',
  billingInformation: '',
  billingAddress: '',
  orgNumber: undefined,
  invoiceTo: ProjectInvoiceTo.ORGANIZATION,
  oracleProjectNumber: undefined,
  availableServicesOverride: [],
};

export const ProjectForm: FC<ProjectFormProps> = ({
  onSubmit,
  trigger,
  onFormChange,
  onValidationChange,
  validationErrors = {},
}) => {
  const { organizations = [] } = useOrganizations();
  const [errors, setErrors] = useState<Partial<Record<keyof CreateProjectFormData, string>>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState<CreateProjectFormData>(defaultValues);
  const [isOpen, setIsOpen] = useState(false);
  const [isNonAecomUser, setIsNonAecomUser] = useState(false);
  const isAecomUser = !isNonAecomUser;
  const formOptions = useFormOptions(organizations);
  // Validate that existing projects names are not used in the form
  const { data: projectsData } = useGetProjectsQuery(
    {
      page: 'all',
      search: '',
      isHidden: true,
    },
    { skip: !isOpen },
  );
  const existingProjectNames = useMemo(() => {
    return projectsData?.results?.map((project: Project) => project.name) || [];
  }, [projectsData]);

  const handleFormSubmit = async () => {
    // Validate form and set errors if validation fails
    const validation = validateProjectForm(formData, existingProjectNames);

    if (!validation.isValid) {
      // Set validation errors to display them to the user
      setErrors(validation.errors);
      return false;
    }

    setIsSubmitting(true);
    try {
      // Handle org_number validation - ensure it's within valid range
      let orgNumber = null;
      if (formData.orgNumber) {
        const numValue = Number(formData.orgNumber);
        if (numValue > 2147483647) {
          throw new Error('Organization number must be less than or equal to 2,147,483,647');
        }
        if (numValue < 0) {
          throw new Error('Organization number must be a positive number');
        }
        orgNumber = numValue;
      }

      const createProjectRequest: CreateProjectRequest = {
        name: formData.name,
        address: formData.address || null,
        country: formData.country,
        specifications: formData.specifications,
        constructionTypes: formData.constructionTypes,
        organizationId: formData.organizationId,
        billingInformation: formData.billingInformation || null,
        billingAddress: formData.billingAddress || null,
        orgNumber: orgNumber,
        invoiceTo: formData.invoiceTo,
        oracleId: formData.oracleProjectNumber || null,
        availableServicesOverride: formData.availableServicesOverride,
      };

      await onSubmit(createProjectRequest);
      // Return true to indicate successful submission
      return true;
    } catch (error) {
      console.error('Error creating project:', error);
      return false;
    } finally {
      setIsSubmitting(false);
    }
  };

  const updateFormData = (field: keyof CreateProjectFormData, value: unknown) => {
    setFormData((prev) => ({ ...prev, [field]: value }));

    // Clear error when field is updated
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const handleProjectNameResolved = useCallback(
    (name: string | null) => {
      updateFormData('name', name ?? '');
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const handleNonAecomToggle = (checked: boolean) => {
    setIsNonAecomUser(checked);
    setFormData((prev) => ({
      ...prev,
      name: '',
      oracleProjectNumber: undefined,
    }));
  };

  const handleReset = () => {
    setFormData(defaultValues);
    setIsNonAecomUser(false);
  };

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
    if (!open) {
      // Reset form data when sheet is closed
      setFormData(defaultValues);
    }
  };

  const handleFieldChange = (field: string | number | symbol, value: unknown) => {
    const newFormData = { ...formData, [field]: value };
    updateFormData(field as keyof CreateProjectFormData, value);

    // Validate all fields and notify parent
    const validation = validateProjectForm(newFormData, existingProjectNames);
    if (onValidationChange) {
      onValidationChange(validation.isValid, validation.errors);
    }
  };

  // Update parent component when form data changes
  useEffect(() => {
    onFormChange(formData);
  }, [formData, onFormChange]);

  // Merge external validation errors with local errors
  useEffect(() => {
    if (Object.keys(validationErrors).length > 0) {
      setErrors((prev) => ({ ...prev, ...validationErrors }));
    }
  }, [validationErrors]);

  // Validate form on initial load and notify parent
  useEffect(() => {
    const validation = validateProjectForm(formData, existingProjectNames);
    if (onValidationChange) {
      onValidationChange(validation.isValid, validation.errors);
    }
  }, [formData, existingProjectNames, onValidationChange]);

  return (
    <SheetForm
      title="Create New Project"
      description="Fill in the details to create a new project"
      trigger={trigger}
      onSubmit={handleFormSubmit}
      onReset={handleReset}
      submitLabel="Create Project"
      resetLabel="Reset"
      isSubmitting={isSubmitting}
      showReset={true}
      open={isOpen}
      onOpenChange={handleOpenChange}
    >
      <div className="space-y-6">
        <div>
          <label className="flex cursor-pointer select-none items-center gap-2 text-sm font-medium">
            <input
              type="checkbox"
              checked={isNonAecomUser}
              onChange={(e) => handleNonAecomToggle(e.target.checked)}
              className="cursor-pointer"
            />
            Non-AECOM Projects
          </label>
          <p className="mt-1 text-xs text-muted-foreground">
            Skips the Oracle project lookup and lets you name the project freely. Use this for test
            projects as well as for projects that are not tied to AECOM.
          </p>
        </div>

        {isAecomUser && (
          <OracleFields
            formData={formData}
            errors={errors}
            onFieldChange={handleFieldChange}
            onProjectNameResolved={handleProjectNameResolved}
          />
        )}

        <BasicInformationFields
          formData={formData}
          errors={errors}
          onFieldChange={handleFieldChange}
          countryOptions={formOptions.countryOptions}
          organizationOptions={formOptions.organizationOptions}
          isAecomUser={isAecomUser}
        />

        <ProjectDetailsFields
          formData={formData}
          errors={errors}
          onFieldChange={handleFieldChange}
          specificationOptions={formOptions.specificationOptions}
          constructionTypeOptions={formOptions.constructionTypeOptions}
        />

        <BillingInformationFields
          formData={formData}
          errors={errors}
          onFieldChange={handleFieldChange}
          invoiceToOptions={formOptions.invoiceToOptions}
        />

        <ServicesFields
          formData={formData}
          errors={errors}
          onFieldChange={handleFieldChange}
          servicesOptions={formOptions.servicesOptions}
        />
      </div>
    </SheetForm>
  );
};
